// orchestrator_fullstack/frontend/src/app/admin/AdminRouter.tsx
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import AdminLayout from "./AdminLayout";

// Keep your existing guard (don’t break current wiring)
import AdminGuard from "./AdminGuard";

// Add JWT-only auth + RBAC route gating (Phase 1 UI enforcement)
import { RequireAuth } from "../auth/RequireAuth";
import { RequirePermission } from "../auth/RequirePermission";

// Existing concrete pages (non-lazy)
import OverviewPage from "./OverviewPage";

import AdminIngressAppsPage from "./ingress/AdminIngressAppsPage";
import AdminIngressAppDetailPage from "./ingress/AdminIngressAppDetailPage";
import AdminIngressRequestsPage from "./ingress/AdminIngressRequestsPage";
import AdminIngressRequestDetailPage from "./ingress/AdminIngressRequestDetailPage";
import AdminIngressFeedbackPage from "./ingress/AdminIngressFeedbackPage";
import AdminIngressStarterAppsPage from "./ingress/AdminIngressStarterAppsPage";

import AdminObservabilityPlatformHealthPage from "./observability/AdminObservabilityPlatformHealthPage";
import AdminObservabilityProviderHealthPage from "./observability/AdminObservabilityProviderHealthPage";
import AdminObservabilityFlowVersionHealthPage from "./observability/AdminObservabilityFlowVersionHealthPage";
import AdminObservabilityPolicyFeedbackPage from "./observability/AdminObservabilityPolicyFeedbackPage";
import UsersRolesPage from "./oidc/UsersRolesPage";
import UsersActivePage from "./oidc/UsersActivePage";
import TenantsOidcPage from "./oidc/TenantsOidcPage";
import { useAuth } from "../auth/AuthProvider";
// ----------------------
// Lazy Imports (keep your existing bundle strategy)
// ----------------------
function AdminIndexRedirect() {
  const { state } = useAuth();
  const roles = state.roles || [];
  if (roles.includes("platform-admin") || roles.includes("admin")) {
    return <Navigate to="overview" replace />;
  }
  if (roles.includes("operator")) {
    return <Navigate to="observability/platform" replace />;
  }
  return <Navigate to="overview" replace />;
}
// Workflow Builder (flows + flow_runs engine)
const WorkflowBuilderPage = lazy(() => import("./workflow/WorkflowBuilderPage"));

// Providers Registry page (platform-admin only)
const ProvidersPage = lazy(() => import("./providers/ProvidersPage"));

// Existing Admin pages
const ComposerPage = lazy(() => import("@/features/composer/ComposerPage"));
const TemplatesGallery = lazy(() => import("../../features/templates/TemplatesGallery")) as any;
const DatasetsPage = lazy(() => import("./DatasetsPage"));
const DatasetManager = lazy(() => import("../../features/datasets/Manager"));
const EvalCreator = lazy(() => import("../../features/evals/Creator"));
const ResultsDashboard = lazy(() => import("../../features/evals/ResultsDashboard"));
const ExperimentsPage = lazy(() => import("../../features/experiments/ExperimentsPage"));
const ResultsBrowser = lazy(() => import("./ResultsBrowser"));
const EvalsPage = lazy(() => import("./EvalsPage"));
const FeedbackPage = lazy(() => import("./FeedbackPage"));

// Governance pages (lists)
const GovernancePoliciesPage = lazy(() => import("./governance/PoliciesPage"));
const GovernanceBindingsPage = lazy(() => import("./governance/BindingsPage"));
const GovernanceDecisionLogPage = lazy(() => import("./governance/DecisionLogPage"));

// Inline components
const Runs = () => <div className="text-sm">Runs list coming soon.</div>;

export default function AdminRouter() {
  const noopOnUse = () => {};

  return (
    <Suspense fallback={<div className="p-6">Loading admin…</div>}>
      {/* Keep existing guard in place */}
      <AdminGuard>
        {/* Admin UI is JWT-only per Phase 1 hardening */}
        <RequireAuth>
          {/* Base: /app/admin/* requires tenant admin OR platform admin */}
          <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
            {/* IMPORTANT:
                App.tsx mounts this at path="/app/admin/*"
                Therefore ALL routes here must be RELATIVE (no "/admin/..." prefix).
             */}
            <Routes>
              <Route element={<AdminLayout />}>
                {/* /app/admin -> /app/admin/overview */}
                <Route index element={<AdminIndexRedirect />} />

                {/* Overview (admin + platform-admin) */}
                <Route path="overview" element={<OverviewPage />} />

                {/* Workflow Builder (tenant-admin/admin + platform-admin) */}
                <Route
                  path="workflow"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <WorkflowBuilderPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="workflows"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <WorkflowBuilderPage />
                    </RequirePermission>
                  }
                />

                {/* Providers Registry (platform-admin only) */}
                <Route
                  path="providers"
                  element={
                    <RequirePermission anyOf={["admin", "platform-admin"]}>
                      <ProvidersPage />
                    </RequirePermission>
                  }
                />

                {/* Ingress Admin (tenant-admin/admin + platform-admin).
                    Note: you can loosen view-only later, but this matches your “admin shell” gating. */}
                <Route
                  path="ingress/apps"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminIngressAppsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="ingress/apps/:appId"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminIngressAppDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="ingress/requests"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminIngressRequestsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="ingress/requests/:requestId"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminIngressRequestDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="ingress/feedback"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminIngressFeedbackPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="ingress/starter-apps"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminIngressStarterAppsPage />
                    </RequirePermission>
                  }
                />

                {/* Observability (operator+ allowed, even though inside admin shell) */}
                <Route
                  path="observability/platform"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminObservabilityPlatformHealthPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="observability/providers"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminObservabilityProviderHealthPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="observability/flows"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminObservabilityFlowVersionHealthPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="observability/policy-feedback"
                  element={
                    <RequirePermission anyOf={["operator", "admin", "platform-admin"]}>
                      <AdminObservabilityPolicyFeedbackPage />
                    </RequirePermission>
                  }
                />
                <Route path="observability" element={<Navigate to="observability/platform" replace />} />

                {/* Legacy composer (tenant-admin/admin + platform-admin) */}
                <Route
                  path="composer"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <ComposerPage />
                    </RequirePermission>
                  }
                />

                {/* Templates (admin+) */}
                <Route
                  path="templates"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <TemplatesGallery onUse={noopOnUse} />
                    </RequirePermission>
                  }
                />

                {/* Datasets (admin+) */}
                <Route
                  path="datasets"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <DatasetsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="datasets/manager"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <DatasetManager />
                    </RequirePermission>
                  }
                />

                {/* Evals (admin+) */}
                <Route
                  path="evals/create"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <EvalCreator />
                    </RequirePermission>
                  }
                />
                <Route
                  path="evals/dashboard"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <ResultsDashboard />
                    </RequirePermission>
                  }
                />
                <Route
                  path="evals"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <EvalsPage />
                    </RequirePermission>
                  }
                />

                {/* Experiments (admin+) */}
                <Route
                  path="experiments"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <ExperimentsPage />
                    </RequirePermission>
                  }
                />

                {/* Runs (viewer+ should eventually see runs outside admin shell; keep admin+ here for stability) */}
                <Route
                  path="runs"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <Runs />
                    </RequirePermission>
                  }
                />

                {/* Results (admin+) */}
                <Route
                  path="results"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <ResultsBrowser />
                    </RequirePermission>
                  }
                />

                {/* Feedback (admin+) */}
                <Route
                  path="feedback"
                  element={
                    <RequirePermission anyOf={["admin", "platform-admin"]}>
                      <FeedbackPage />
                    </RequirePermission>
                  }
                />

                {/* Governance (platform-admin only per your requirements) */}
                <Route
                  path="governance/policies"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <GovernancePoliciesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="governance/bindings"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <GovernanceBindingsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="governance/bindings/new"
                  element={<Navigate to="governance/bindings?new=1" replace />}
                />
                <Route
                  path="governance/decisions"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <GovernanceDecisionLogPage />
                    </RequirePermission>
                  }
                />
                
                {/* Settings / OIDC (preferred canonical paths) */}
                <Route
                  path="users-roles"
                  element={
                    <RequirePermission anyOf={["admin", "platform-admin"]}>
                      <UsersRolesPage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="oidc/users-roles"
                  element={
                    <RequirePermission anyOf={["admin", "platform-admin"]}>
                      <UsersRolesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="users-active"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <UsersRolesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="oidc/users-active"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <UsersActivePage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="oidc/tenants-oidc"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <TenantsOidcPage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="oidc/tenants-oidc"
                  element={
                    <RequirePermission anyOf={["platform-admin"]}>
                      <TenantsOidcPage />
                    </RequirePermission>
                  }
                />

                {/* Catch-all under /app/admin/* */}
                <Route path="*" element={<Navigate to="overview" replace />} />
              </Route>
            </Routes>
          </RequirePermission>
        </RequireAuth>
      </AdminGuard>
    </Suspense>
  );
}